import React, { useState, useEffect, useRef } from 'react';
import { FaSearch } from 'react-icons/fa';
import { IoIosArrowUp, IoIosArrowDown, IoIosClose } from 'react-icons/io';

export interface DropdownOption {
    value: string;
    label: string;
    disabled?: boolean;
}

interface DropdownProps {
    options: DropdownOption[];
    isSearchable?: boolean;
    placeholder?: string;
    onSelect: (option: DropdownOption) => void;
    value?: string; // Expect the ID here
    disabled?: boolean;
    allowClear?: boolean;
    onClear?: () => void;
}

const Dropdown: React.FC<DropdownProps> = ({
    options,
    isSearchable = false,
    allowClear = false,
    placeholder = 'Select an option',
    onSelect,
    onClear,
    value,
    disabled = false,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchValue, setSearchValue] = useState('');
    const [selectedValue, setSelectedValue] = useState<string | null>(null);
    const [dropdownPosition, setDropdownPosition] = useState<'down' | 'up'>('down');
    const dropdownRef = useRef<HTMLDivElement>(null);
    const dropdownListRef = useRef<HTMLDivElement>(null);

    const filteredOptions = (options || []).filter((option) => {
        // Guard against null/undefined options or labels that aren't strings
        if (!option || typeof option.label !== 'string') {
            return false;
        }
        return option.label.toLowerCase().includes(searchValue.toLowerCase());
    });


    const calculateDropdownPosition = () => {
        if (!dropdownRef.current) return;
        
        const rect = dropdownRef.current.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const dropdownHeight = 200; // Approximate height of dropdown list
        
        // Check if there's enough space below the dropdown
        const spaceBelow = viewportHeight - rect.bottom;
        const spaceAbove = rect.top;
        
        // If there's not enough space below but enough space above, position upward
        if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
            setDropdownPosition('up');
        } else {
            setDropdownPosition('down');
        }
    };

    const handleSelect = (option: DropdownOption) => {
        // Guard against invalid option or disabled option
        if (!option || typeof option.label !== 'string' || option.disabled) {
            return;
        }
        setSelectedValue(option.label);
        setIsOpen(false);
        setSearchValue('');
        onSelect(option); // Pass the selected ID
    };

    const handleClear = (e: React.MouseEvent) => {
        e.stopPropagation();
        setSelectedValue(null);
        setSearchValue('');
        setIsOpen(false);
        // Call onSelect with empty value to clear parent state
        onSelect({ value: '', label: '' });
        if (onClear) {
            onClear();
        }
    };

    const handleDropdownToggle = () => {
        if (!disabled) {
            if (!isOpen) {
                calculateDropdownPosition();
            }
            setIsOpen(!isOpen);
        }
    };

    const handleClickOutside = (event: MouseEvent) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
            setIsOpen(false);
        }
    };

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        window.addEventListener('resize', calculateDropdownPosition);
        window.addEventListener('scroll', calculateDropdownPosition, true);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            window.removeEventListener('resize', calculateDropdownPosition);
            window.removeEventListener('scroll', calculateDropdownPosition, true);
        };
    }, []);

    useEffect(() => {
        if (value) {
            // Try to find option by value (ID) or label (name)
            const selectedOption = options.find(option => option.value === value || option.label === value);
            // If found in options, use the label. Otherwise, display the value as-is (for cases with no options)
            setSelectedValue(selectedOption ? selectedOption.label : value);
        } else {
            setSelectedValue(null);
        }
    }, [value, options]); // Update when value or options change

    return (
        <div ref={dropdownRef} className="relative w-full">
            <div
                className={`text-sm cursor-pointer py-3 px-4 flex justify-between items-center  border border-gray-300 ${disabled ? 'bg-gray-100' : 'bg-white'} ${
                    isOpen 
                        ? (dropdownPosition === 'up' ? 'rounded-b-lg border-gray-500' : 'rounded-t-lg border-gray-500')
                        : 'rounded-lg'
                }`}
                onClick={handleDropdownToggle}
                style={{
                    color: selectedValue ? '#333333' : '#A2A2A2',
                    borderBottomLeftRadius: isOpen && dropdownPosition === 'down' ? '0px' : '8px',
                    borderBottomRightRadius: isOpen && dropdownPosition === 'down' ? '0px' : '8px',
                    borderTopLeftRadius: isOpen && dropdownPosition === 'up' ? '0px' : '8px',
                    borderTopRightRadius: isOpen && dropdownPosition === 'up' ? '0px' : '8px',
                }}
            >
                <span>{selectedValue ? selectedValue : placeholder}</span>
                <div className="flex items-center gap-1">
                    {allowClear && selectedValue && !disabled && (
                        <button
                            onClick={handleClear}
                            className="hover:bg-gray-200 rounded-full p-1 transition-colors"
                        >
                            <IoIosClose size={20} />
                        </button>
                    )}
                    <span>
                        {isOpen 
                            ? (dropdownPosition === 'up' ? <IoIosArrowDown /> : <IoIosArrowUp />)
                            : <IoIosArrowDown />
                        }
                    </span>
                </div>
            </div>
            {isOpen && (
                <div 
                    ref={dropdownListRef}
                    className={`absolute z-10 left-0 right-0 bg-white border border-gray-500 shadow-lg ${
                        dropdownPosition === 'up' 
                            ? 'bottom-full rounded-t-lg border-b-0' 
                            : 'top-full rounded-b-lg border-t-0'
                    }`}
                >
                    {isSearchable && (
                        <div className="relative mb-2 p-2">
                            <input
                                type="text"
                                value={searchValue}
                                disabled={disabled}
                                onChange={(e) => setSearchValue(e.target.value)}
                                placeholder="Search here..."
                                className="w-full pl-4 pr-10 py-2 border border-gray-300 rounded-md text-sm focus:outline-none"
                                style={{ color: '#333333' }}
                            />
                            <FaSearch
                                className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400"
                            />
                        </div>
                    )}
                    <ul className="max-h-48 overflow-y-auto border-r border-b border-l border-gray-200">
                        {filteredOptions.length > 0 ? (
                            filteredOptions.map((option) => (
                                <li
                                    key={option.value}
                                    className={`py-3 px-4 text-sm border-t border-[#f2f2f2] ${
                                        option.disabled 
                                            ? 'text-gray-400 cursor-not-allowed opacity-60' 
                                            : 'cursor-pointer hover:bg-gray-100'
                                    }`}
                                    onClick={disabled || option.disabled ? undefined : () => handleSelect(option)}
                                >
                                    {option.label}
                                </li>
                            ))
                        ) : (
                            <li className="py-2 px-4 text-gray-500 text-sm">No results found</li>
                        )}
                    </ul>
                </div>
            )}
        </div>
    );
};

export default Dropdown;
